import get from 'hs-lodash/get';
import { Component } from 'react';
import { Text } from 'foundations-components/components/Text';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import SkeletonText from 'automation-ui-core/components/SkeletonText';
import PropertyName from 'automation-ui-references/object-type-properties/PropertyName';
import ObjectTypeName from 'automation-ui-references/object-types/ObjectTypeName';
import { queryObjectTypeByPropertyTypeId } from 'automation-ui-references/object-type-properties/ObjectTypesQueries';
import { SET, INCREASE } from 'automation-ui-action-features/actions/set-property/SetPropertyActionOperations';
import { getSetPropertyActionDetailMessage } from 'automation-ui-action-features/actions/set-property/utils/getSetPropertyActionDetailMessage';
import wireTargetProperty from 'automation-ui-action-features/actions/set-property/wireTargetProperty';
import { wireExternalOptionValues } from 'automation-ui-action-features/external-options/externalOptionsDecorator';
import * as PropertyValueTypes from 'automation-ui-flow-core/fields/PropertyValueTypes';
import wireAssociationDefinitions from 'automation-ui-associations/hocs/wireAssociationDefinitions';
import associationSupportsCustomLabels from 'automation-ui-associations/utils/associationSupportsCustomLabels';
import CRMPropertyFieldTypes from 'automation-ui-flow-core/fields/CRMPropertyFieldTypes';
import getAssociationLabel from 'automation-ui-associations/utils/getAssociationLabel';
import findAssociationDefinitionFromSpec from 'automation-ui-associations/utils/findAssociationDefinitionFromSpec';
import inferPropertyActionOperationType from 'automation-ui-action-features/actions/set-property/utils/inferPropertyActionOperationType';
import PersonalizedTextView from 'automation-ui-action-features/personalized-text/PersonalizedTextView';
import { compose } from 'redux';
import DataSourcePropertyName from 'automation-ui-formula-editor/data-sources/DataSourcePropertyName';
import DataSourceName from 'automation-ui-formula-editor/data-sources/DataSourceName';
import I18n from 'I18n';
import { getIsAssociation } from 'automation-ui-action-features/actions/set-property/utils/associationUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const convertPropsForWireAssociationDefinitions = WrappedComponent => props => /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
  fromObjectTypeIds: [props.flowObjectTypeId]
}));
const convertPropsForWireExternalOptions = WrappedComponent => props => inferPropertyActionOperationType(props.actionMetadata) !== SET ? /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
  propertyTypeId: props.targetObjectTypeId // required for queryObjectTypeByPropertyTypeId
})) : /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
  externalOptionValue: props.actionMetadata.getValueValue(),
  property: props.targetProperty,
  propertyTypeId: props.targetObjectTypeId // also for queryObjectTypeByPropertyTypeId
}));
class SetPropertyActionDetail extends Component {
  constructor(...args) {
    super(...args);
    this.getFieldDataOptions = () => {
      const {
        actionMetadata,
        actionOutputs,
        getActionLabelById
      } = this.props;
      const sourceActionId = actionMetadata.getValueIdentifierActionId();
      const inputSource = actionOutputs.find(({
        actionId
      }) => actionId === sourceActionId);
      if (!inputSource) {
        return {
          inputActionLabel: /*#__PURE__*/_jsx(SkeletonText, {}),
          inputValueName: /*#__PURE__*/_jsx(SkeletonText, {})
        };
      }
      const inputSourceOutputName = actionMetadata.getValueIdentifierDataKey();
      const inputSourceOutput = inputSource.outputFieldDefinitions.find(({
        typeDefinition: {
          name
        }
      }) => name === inputSourceOutputName);
      const inputSourceOutputLabel = get(inputSourceOutput, 'typeDefinition.label', inputSourceOutputName);
      return {
        inputActionLabel: /*#__PURE__*/_jsx(Text, {
          as: "span",
          typography: "heading-50",
          children: getActionLabelById({
            actionId: sourceActionId
          })
        }),
        inputValueName: /*#__PURE__*/_jsx(Text, {
          as: "span",
          typography: "heading-50",
          children: inputSourceOutputLabel
        })
      };
    };
    this.getDataTokenOptions = () => {
      const {
        actionMetadata
      } = this.props;
      return {
        inputDataPropertyName: /*#__PURE__*/_jsx(DataSourcePropertyName, {
          token: actionMetadata.getValuePropertyToken()
        }),
        inputDataSourceLabel: /*#__PURE__*/_jsx(DataSourceName, {
          token: actionMetadata.getValuePropertyToken()
        })
      };
    };
  }
  render() {
    const {
      actionMetadata,
      associationDefinitionsQuery,
      externalOptionValues,
      flowObjectTypeId,
      messageLangPath,
      objectTypeDefinition,
      options,
      targetObjectTypeId,
      targetProperty,
      targetPropertyValueLabel
    } = this.props;
    const targetPropertyName = actionMetadata.getTargetPropertyName();
    const sourcePropertyName = actionMetadata.getValuePropertyName();
    const isNotFieldData = actionMetadata.getValueType() !== PropertyValueTypes.FIELD_DATA;
    const isPercentageValue = !!targetProperty && targetProperty.fieldType === CRMPropertyFieldTypes.NUMBER && targetProperty.numberDisplayHint === 'percentage';
    let valueOverride = targetPropertyValueLabel || '';

    // External Options value takes precedence.
    if (externalOptionValues.references) {
      const overrideArray = externalOptionValues.references.map(({
        label
      }) => label);
      const valueOverrideI18nList = I18n.formatList(overrideArray);
      if (valueOverrideI18nList) {
        valueOverride = valueOverrideI18nList;
      }
    }
    const isAssociation = getIsAssociation({
      metadata: actionMetadata
    });
    const isIncreaseAction = inferPropertyActionOperationType(actionMetadata) === INCREASE;
    const showAssociationLabel = Boolean(targetObjectTypeId && isAssociation && associationSupportsCustomLabels(flowObjectTypeId, targetObjectTypeId) && !isIncreaseAction);

    // NOTE(bheng): Some languages such as Dutch require capitalized nouns.
    const enrolledObjectTypeOptions = {
      enrolledObjectType: /*#__PURE__*/_jsx(ObjectTypeName, {
        objectTypeId: flowObjectTypeId
      }),
      enrolledObjectTypeCapitalized: /*#__PURE__*/_jsx(ObjectTypeName, {
        isCapitalized: true,
        objectTypeId: flowObjectTypeId
      })
    };
    const targetObjectTypeOptions = targetObjectTypeId ? {
      targetObjectType: /*#__PURE__*/_jsx(ObjectTypeName, {
        objectTypeId: targetObjectTypeId
      }),
      targetObjectTypeCapitalized: /*#__PURE__*/_jsx(ObjectTypeName, {
        isCapitalized: true,
        objectTypeId: targetObjectTypeId
      }),
      targetObjectTypePlural: /*#__PURE__*/_jsx(ObjectTypeName, {
        isPlural: true,
        objectTypeId: targetObjectTypeId
      }),
      targetObjectTypePluralAndCapitalized: /*#__PURE__*/_jsx(ObjectTypeName, {
        isCapitalized: true,
        isPlural: true,
        objectTypeId: targetObjectTypeId
      })
    } : {
      targetObjectType: /*#__PURE__*/_jsx(SkeletonText, {}),
      targetObjectTypeCapitalized: /*#__PURE__*/_jsx(SkeletonText, {}),
      targetObjectTypePlural: /*#__PURE__*/_jsx(SkeletonText, {}),
      targetObjectTypePluralAndCapitalized: /*#__PURE__*/_jsx(SkeletonText, {})
    };
    const messageOptions = Object.assign({}, options, this.getFieldDataOptions(), this.getDataTokenOptions(), {
      associatedLabel: showAssociationLabel && /*#__PURE__*/_jsx("b", {
        children: objectTypeDefinition ? getAssociationLabel({
          associationDefinition: findAssociationDefinitionFromSpec(actionMetadata.getTargetAssociation(), associationDefinitionsQuery),
          toObjectTypeDefinition: objectTypeDefinition
        }) : /*#__PURE__*/_jsx(SkeletonText, {})
      }),
      amount: /*#__PURE__*/_jsx(Text, {
        as: "span",
        typography: "heading-50",
        children: Math.abs(actionMetadata.getValueAmount())
      })
    }, enrolledObjectTypeOptions, {
      sourcePropertyName: sourcePropertyName && isNotFieldData ? /*#__PURE__*/_jsx(PropertyName, {
        propertyName: sourcePropertyName,
        objectTypeId: flowObjectTypeId
      }) : null
    }, targetObjectTypeOptions, {
      targetPropertyName: targetObjectTypeId && targetPropertyName ? /*#__PURE__*/_jsx(PropertyName, {
        propertyName: targetPropertyName,
        objectTypeId: targetObjectTypeId
      }) : /*#__PURE__*/_jsx(SkeletonText, {}),
      value: /*#__PURE__*/_jsx(Text, {
        as: "span",
        typography: "heading-50",
        children: isPercentageValue ? /*#__PURE__*/_jsx(FormattedPercentage, {
          precision: 2,
          value: Number(valueOverride) * 100
        }) : /*#__PURE__*/_jsx(PersonalizedTextView, {
          propertyTypeId: flowObjectTypeId,
          text: valueOverride
        })
      })
    });
    // targetObjectTypeId will be undefined until association definitions are loaded
    return getSetPropertyActionDetailMessage({
      actionMetadata,
      isActionPreview: messageLangPath === 'actionPreview',
      isAssociation,
      options: messageOptions,
      showAssociationLabel
    });
  }
}

// NOTE(bheng): Composition order is important. The previous decorator provides
//   props required for the next one. Please be intentional when modifying this
//   section. For example, wireExternalOptions needs the targetProperty from
//   wireTargetProperty.
SetPropertyActionDetail.defaultProps = {
  messageLangPath: 'actionDetail',
  actionOutputs: []
};
export default compose(convertPropsForWireAssociationDefinitions, wireAssociationDefinitions, wireTargetProperty, convertPropsForWireExternalOptions, wireExternalOptionValues, queryObjectTypeByPropertyTypeId)(SetPropertyActionDetail);